import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataLoadURL } from '../../model/dataload-url';
import { EcpWorkflowEnvironmentService } from '../environment/environment.service';
import { firstValueFrom, Observable } from 'rxjs';

import { Info, RequestHeaderConst, RouterUrlConstant } from '../../constant/information.const';

import { StorageWrapperService } from '../storage-wrapper/storage-wrapper.service';
import { UserService } from '../user/user.service';
import { ApiParam } from '../../model/api-param';

/**
 *
 * API Service
 * @export
 * @ class ApiService
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly storageWrapperService: StorageWrapperService,
    private readonly userService: UserService,
    private readonly environmentService: EcpWorkflowEnvironmentService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mergeHeader(hostName: string, header: any, url: string): HttpHeaders {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preHeader = this.getHeaders(this.environmentService.ecpWorkflowEnvironment, hostName) as any;
    const headerObject = {};
    const cachedToken = this.userService.token;

    if (cachedToken) {
      headerObject[RequestHeaderConst.authorization] = `${cachedToken}`;
      // Temporarily duplicating session guid. Authorization will eventually become the JWT
      headerObject[RequestHeaderConst.dxSessionId] = `${cachedToken}`;
    }

    if (url === RouterUrlConstant.apiLoginUrl) {
      delete preHeader.Authorization;
      delete preHeader['Session-Id'];
    }

    for (const key in preHeader) {
      if (preHeader.hasOwnProperty(key)) {
        const element = preHeader[key];
        headerObject[key] = element;
      }
    }

    for (const key in header) {
      if (header.hasOwnProperty(key)) {
        const element = header[key];
        if (element !== undefined) {
          headerObject[key] = element;
        } else if (headerObject.hasOwnProperty(key)) {
          delete headerObject[key];
        }
      }
    }

    return new HttpHeaders(headerObject);
  }

  public async requestApi<T>({ urlObj, uiElement, body, queryParam, urlIds, callback, errorCallback }: ApiParam): Promise<T> {
    return (
      firstValueFrom(this.request<T>({ urlObj, uiElement, body, queryParam, urlIds }))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((result: any) => {
          if (callback) {
            callback(result);
          }

          return result;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((errorResponse: any) => {
          if (errorCallback) {
            errorCallback(errorResponse);
          }
          throw errorResponse;
        })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public request<T>({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<T> {
    const queryParams = queryParam || {};
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);
    urlObj.method = urlObj.method || RequestHeaderConst.methodType;
    let params: HttpParams;

    if (Object.keys(queryParams).length > 0) {
      // need to update queryParams type in api-param -- queryParam?: {[param: string]: string | readonly string[]};
      params = new HttpParams({ fromObject: queryParams });
    }

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }
    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    // if (urlObj.url === RouterUrlConstant.login) {
    //   headers.delete(RequestHeaderConst.authorization);
    // }

    return this.httpClient.request(urlObj.method, url, {
      body,
      headers,
      params
    }) as Observable<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getImage({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<Blob> {
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }

    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    return this.httpClient.get(url, {
      headers,
      responseType: 'blob'
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public postImage({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<Blob> {
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }

    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    return this.httpClient.post(url, body, {
      headers,
      responseType: 'blob'
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public downloadFile({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<Blob> {
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }

    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    return this.httpClient.get(url, {
      headers,
      responseType: 'blob'
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public uploadFile(urlObj: DataLoadURL, formData: FormData, urlIds: { [header: string]: string }): any {
    const header = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);
    let url = this.GetFullURL(urlObj.hostName, urlObj.url, urlIds);
    // TODO: Need to handle proper way once document issue resolved.
    url = url.replace('/api', '/stream/api');
    return this.httpClient.post(url, formData, { headers: header });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public uploadFiles(urlObj: DataLoadURL, files: File[], urlIds: { [header: string]: string }): any {
    // const header = this.getMultipartHttpHeaders();
    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }
    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlIds);
    const formData: FormData = new FormData();
    files.forEach((file) => {
      formData.append(Info.file, file, file.name);
    });
    return this.httpClient.post(url, formData, { headers });
  }

  // Multipart Header
  // eslint-disable-next-line @typescript-eslint/ban-types
  public getMultipartHttpHeaders(contentType = RequestHeaderConst.multipart): {} {
    const token = this.userService.token;

    if (token === undefined) {
      if (contentType === '') {
        return {};
      }
      return { contentTypeName: contentType };
    }

    const header = {
      // TODO: This should be a proper CUID Issued JWT
      [RequestHeaderConst.authorization]: `${token}`,
      [RequestHeaderConst.dxSessionId]: `${token}`,
      [RequestHeaderConst.pragma]: RequestHeaderConst.cacheControlValue,
      [RequestHeaderConst.allowHeaderName]: RequestHeaderConst.allowHeaderValue,
      [RequestHeaderConst.allowOriginName]: RequestHeaderConst.allowOriginValue,
      [RequestHeaderConst.cacheControlName]: RequestHeaderConst.cacheControlValue
    };

    for (const key of Object.keys(header)) {
      if (!header[key]) {
        delete header[key];
      }
    }
    return header;
  }
  /** Functiton to return full url */
  public GetFullURL = (
    objName: string,
    url: string,
    urlObj?: {
      [header: string]: string;
    }
  ): string => {
    const apiData = this.environmentService.ecpWorkflowEnvironment[objName];
    const user = this.userService.user;
    let fullURL = url;
    const appNumber = this.storageWrapperService.get<string>(Info.appNumber);
    if (appNumber) {
      fullURL = fullURL.replace(/{{appNumber}}/gi, appNumber);
      fullURL = fullURL.replace(/{{loanNumber}}/gi, appNumber);
    }
    if (user) {
      if (user.qFileId) {
        fullURL = fullURL.replace(/{{QFileID}}/gi, user.qFileId);
      }
      if (user.dataSourceId) {
        fullURL = fullURL.replace(/{{dataSourceId}}/gi, user.dataSourceId);
      }
      if (user.borrowerId) {
        fullURL = fullURL.replace(/{{borrowerId}}/gi, user.borrowerId);
      }
      if (user.userID) {
        fullURL = fullURL.replace(/{{memberId}}/gi, user.userID.toString());
      }
      if (user.loanID) {
        fullURL = fullURL.replace(/{{loanId}}/gi, user.loanID.toString());
      }
    }

    if (apiData === undefined) {
      throw Error(Info.hostNotDefined);
    }

    if (urlObj !== undefined) {
      for (const key in urlObj) {
        if (urlObj.hasOwnProperty(key)) {
          const element: string = urlObj[key];
          const urlKey = new RegExp(`{${key}}`, 'g');
          if (fullURL.indexOf(`{${key}}`) > 0) {
            fullURL = fullURL.replace(urlKey, element);
          }
        }
      }
    }

    const http = apiData.isHTTPS ? RequestHeaderConst.https : RequestHeaderConst.http;
    const port = apiData.port === undefined || apiData.port === '' ? '' : `:${apiData.port}`;
    const suffix = apiData.suffix === undefined ? '' : `/${apiData.suffix}`;
    return `${http + apiData.domain + port}${suffix}/${fullURL}`;
  };

  /**
   * IE compatible method to convert blob to string
   * @param blob input blob
   * @param callback fn to utilize the result of blob reading
   * @param errorHandler optional handler for blob reading failure
   */
  public blobToString(blob: Blob, callback: (result: string) => void, errorHandler?: () => void): void {
    const reader = new FileReader();
    // triggered once blob done loading
    reader.addEventListener('loadend', () => {
      if (reader && reader.result) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        callback(reader.result.toString());
      } else {
        errorHandler();
      }
    });
    reader.readAsText(blob);
  }

  /**
   * Set temporary headers, do we even need a method for this?
   * @param environment
   * @param hostName
   * @returns - temporary headers
   */
  public getHeaders = (environment, hostName: string): object => {
    const apiData = environment[hostName];

    if (apiData === undefined) {
      throw Error('Host data not defined');
    }

    if (apiData.headers === undefined) {
      return {};
    }
    const tempHeader = {};
    Object.assign(tempHeader, apiData.headers);

    return tempHeader;
  };
}
